import { FC, useState } from 'react'
import { useHistory } from 'react-router-dom'

import { useCreatePkgReq } from '@/api/package-req/mutations/useCreatePkgReq'
import { useRejectPkgReq } from '@/api/package-req/mutations/useRejectPkgReq'
import { PkgReqTypeEnum } from '@/api/package-req/types/PkgReq'
import { PkgWarehouse, UserPkg } from '@/api/user-package/types/UserPkg'
import { AppTooltip } from '@/components/AppTooltip'
import PhotoDownloadIcon from '@/components/icons/photo-download.svg?react'
import PhotoIcon from '@/components/icons/pkg-photo.svg?react'
import ReturnIcon from '@/components/icons/pkg-return.svg?react'
import ShrinkIcon from '@/components/icons/pkg-shrink.svg?react'
import UniteIcon from '@/components/icons/pkg-unite.svg?react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/useToast'
import { cn } from '@/lib/utils'
import { RoutePath } from '@/RoutePath'

import { PhotoListDialog } from '../../../../components/PhotoListDialog'
import { CancelActionDialog } from './CancelActionDialog'
import { PkgRequestConfirmDialog } from './PkgRequestConfirmDialog'

const ReqButton = ({
  IconComponent,
  label,
  onClick,
  disabled,
  tootlipContent,
}: {
  IconComponent: FC<React.SVGProps<SVGSVGElement>>
  label: string
  onClick?: () => void
  disabled?: boolean
  tootlipContent?: React.ReactNode
}) => {
  return (
    <AppTooltip triggerProps={{ asChild: true }} content={tootlipContent} asChild side='top'>
      <Button
        icon={<IconComponent className={cn('size-6', !disabled && 'text-icon-primary')} />}
        variant='secondary'
        onClick={disabled ? undefined : onClick}
        className={cn(
          'gap-2 text-xs disabled:pointer-events-auto disabled:cursor-default disabled:hover:shadow-none',
          disabled && 'text-text-disabled cursor-default bg-[#F7F8F9] hover:shadow-none'
        )}
      >
        {label}
      </Button>
    </AppTooltip>
  )
}
interface ReqButtonProps {
  pkg: UserPkg
  loadingCreateReq?: boolean
  onCreateReq: (reqType: PkgReqTypeEnum, comment?: string) => void
  onCancelReq: (reqType: PkgReqTypeEnum, pkgReqId: string) => void
}
const ShrinkButton = ({ pkg, onCreateReq, onCancelReq, loadingCreateReq }: ReqButtonProps) => {
  const [isShrinkDialogOpen, setIsShrinkDialogOpen] = useState(false)
  const { showSuccessToast, showErrorToast } = useToast()

  const handleShrinkConfirm = async (comment: string) => {
    try {
      await onCreateReq(PkgReqTypeEnum.ShrinkRequest, comment)
      setIsShrinkDialogOpen(false)
      showSuccessToast('הבקשה לצמצום התקבלה!', 'נטפל בזה תוך 2 ימי עסקים ונעדכן אותך כשהתהליך יושלם')
    } catch (error) {
      console.error('Error creating shrink request', error)
      showErrorToast('הבקשה לצמצום נכשלה!', 'אנא נסה שנית או פנה לשירות הלקוחות.')
    }
  }

  if (pkg.isReturnRequestActive) {
    return <ReqButton IconComponent={ShrinkIcon} label='צמצום' disabled tootlipContent='יש בקשת החזרה בטיפול' />
  }
  if (pkg.isShrinkRequestApproved) {
    return <ReqButton IconComponent={ShrinkIcon} label='צמצום' disabled tootlipContent='איזה כיף! החבילה שלך כבר צומצמה' />
  }
  if (pkg.isShrinkRequestActive) {
    return (
      <ReqButton
        IconComponent={ShrinkIcon}
        label='ביטול צמצום'
        onClick={() => onCancelReq(PkgReqTypeEnum.ShrinkRequest, pkg.shrinkRequestId!)}
      />
    )
  }

  const isTextile = (pkgCat: string) => ['Underwear', 'Pants', 'Shirt', 'Skirt', 'Jackets', 'Home Textile'].includes(pkgCat)
  const areAllTextile = (contents: typeof pkg.contents) => contents.every(c => isTextile(c.productCategory))
  //textile items cannot be shrunk in UAE warehouse
  const allowedWarehouses = [PkgWarehouse.USA, PkgWarehouse.UK]

  const allowShrinkUae = pkg.warehouse === PkgWarehouse.UAE && !areAllTextile(pkg.contents) // Returns false if all are textile
  const allowShrinkGr = pkg.warehouse === PkgWarehouse.GR && pkg.contents.some(c => c.productCategory === 'Shoes')
  const isMaxShrinked = pkg.isShrinked !== undefined && pkg.isShrinked === true
  const allowShrink = pkg.isShrinked === false || allowedWarehouses.includes(pkg.warehouse) || allowShrinkGr || allowShrinkUae
  if (!isMaxShrinked && allowShrink) {
    return (
      <>
        <ReqButton IconComponent={ShrinkIcon} label='צמצום' onClick={() => setIsShrinkDialogOpen(true)} />
        <PkgRequestConfirmDialog
          requestType={PkgReqTypeEnum.ShrinkRequest}
          isOpen={isShrinkDialogOpen}
          onClose={() => setIsShrinkDialogOpen(false)}
          onConfirm={handleShrinkConfirm}
          isLoading={loadingCreateReq}
        />
      </>
    )
  }
  return <ReqButton IconComponent={ShrinkIcon} label='צמצום' disabled tootlipContent='איזה כיף! החבילה שלך כבר צומצמה ללא תשלום!' />
}

const PhotoButton = ({ pkg, onCreateReq, onCancelReq, loadingCreateReq }: ReqButtonProps) => {
  const [isPhotoDialogOpen, setIsPhotoDialogOpen] = useState(false)
  const [isPhotosGalleryOpen, setIsPhotosGalleryOpen] = useState(false)
  const { showSuccessToast, showErrorToast } = useToast()
  const handlePhotoConfirm = async (notes: string) => {
    try {
      await onCreateReq(PkgReqTypeEnum.PhotoRequest, notes)
      setIsPhotoDialogOpen(false)
      showSuccessToast('בקשת הצילום התקבלה!', 'נעדכן אותך כשהתמונה תהיה מוכנה להורדה')
    } catch (error) {
      console.error('Error creating photo request', error)
      showErrorToast('הבקשה לצילום נכשלה!', 'אנא נסה שנית או פנה לשירות הלקוחות.')
    }
  }

  const photos = pkg.photos && pkg.photos.length > 0 ? pkg.photos : pkg.photo ? [pkg.photo] : []
  if (photos.length > 0) {
    return (
      <>
        <ReqButton
          IconComponent={PhotoDownloadIcon}
          label={photos.length > 1 ? `צילום (${photos.length})` : 'צילום'}
          onClick={() => setIsPhotosGalleryOpen(true)}
        />
        <PhotoListDialog photos={photos} isOpen={isPhotosGalleryOpen} onOpenChange={setIsPhotosGalleryOpen} />
      </>
    )
  }
  if (pkg.isReturnRequestActive) {
    return <ReqButton IconComponent={PhotoIcon} label='צילום' disabled tootlipContent='יש בקשת החזרה בטיפול' />
  }
  if (pkg.isPhotoRequestActive) {
    return (
      <ReqButton
        IconComponent={PhotoIcon}
        label='ביטול צילום'
        onClick={() => onCancelReq(PkgReqTypeEnum.PhotoRequest, pkg.photoRequestId!)}
      />
    )
  }
  return (
    <>
      <ReqButton IconComponent={PhotoIcon} label='צילום' onClick={() => setIsPhotoDialogOpen(true)} />
      <PkgRequestConfirmDialog
        requestType={PkgReqTypeEnum.PhotoRequest}
        isOpen={isPhotoDialogOpen}
        onClose={() => setIsPhotoDialogOpen(false)}
        onConfirm={handlePhotoConfirm}
        isLoading={loadingCreateReq}
      />
    </>
  )
}

const ReturnButton = ({ pkg }: ReqButtonProps) => {
  const history = useHistory()
  const handleCreateReturnReq = () => {
    history.push(`${RoutePath.ClientPkgReturn}/${pkg.id}`)
  }

  if (pkg.isShrinkRequestActive || pkg.isPhotoRequestActive || pkg.isUniteRequestActive) {
    return <ReqButton IconComponent={ReturnIcon} label='החזרה' disabled tootlipContent='יש בקשה אחרת בטיפול' />
  }
  if (pkg.isReturnRequestActive) {
    return <ReqButton IconComponent={ReturnIcon} disabled tootlipContent='לביטול הבקשה אנא פה לשירות הלקוחות' label='החזרה' />
  }
  return <ReqButton IconComponent={ReturnIcon} label='החזרה' onClick={handleCreateReturnReq} />
}
const UniteButton = ({ pkg, onCancelReq }: ReqButtonProps) => {
  return (
    <ReqButton
      IconComponent={UniteIcon}
      label='ביטול בקשת איחוד'
      onClick={() => onCancelReq(PkgReqTypeEnum.UniteRequest, pkg.uniteRequestId!)}
    />
  )
}
interface PkgReqMenuProps {
  pkg: UserPkg
  className?: string
}
export const PkgReqMenu = ({ pkg, className }: PkgReqMenuProps) => {
  const { mutateAsync: createPkgReq, isPending: loadingCreateReq } = useCreatePkgReq()
  const { mutateAsync: rejectPkgReq, isPending: loadingRejectReq } = useRejectPkgReq()
  const { showSuccessToast } = useToast()
  const [reqToCancel, setReqToCancel] = useState<{ reqType: PkgReqTypeEnum; pkgReqId: string } | null>(null)
  const handleRejectPkgReq = async () => {
    await rejectPkgReq({
      packageReqId: reqToCancel!.pkgReqId,
      rejectReason: 'userCanceled',
    })

    setReqToCancel(null)
    showSuccessToast('הבקשה בוטלה בהצלחה!')
  }
  const onCancelReq = async (reqType: PkgReqTypeEnum, pkgReqId: string) => {
    setReqToCancel({ reqType, pkgReqId })
  }
  const handleCreatePkgReq = async (reqType: PkgReqTypeEnum, comment?: string) => {
    await createPkgReq({
      packageId: pkg.id,
      reqType,
      userComment: comment,
    })
  }
  return (
    <div className={cn('flex items-center gap-2', className)}>
      {pkg.isUniteRequestActive ? (
        <UniteButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
      ) : (
        <>
          <ShrinkButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
          <PhotoButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
          <ReturnButton pkg={pkg} onCreateReq={handleCreatePkgReq} loadingCreateReq={loadingCreateReq} onCancelReq={onCancelReq} />
        </>
      )}
      <CancelActionDialog
        isOpen={!!reqToCancel}
        onClose={() => setReqToCancel(null)}
        onConfirm={handleRejectPkgReq}
        actionType={reqToCancel?.reqType}
        isLoading={loadingRejectReq}
      />
    </div>
  )
}
